import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Box, Button, ButtonText, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import {
  AlertCircle, CheckCircle, Clock,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { formatEnglishWaitingSlotStart } from '@/common/utils/formatEnglishDateTime';
import {
  formatOralExamJoinWindowCloseTime,
  type OralExamWaitingTimePhase,
  resolveOralExamWaitingTimePhase,
} from '@/common/utils/oralExam/studentJoinWindow';

import { resolveWaitingLoungeHints } from '@/student/module/utils/oralExamLounge';
import { isSelfServiceRun } from '@/common/utils/oralExam/runConduct';
import { useMeetingConnectionState } from '@/common/components/meeting/MeetingConnectionGuard';
import {
  DRY_RUN_UNAVAILABLE_MESSAGE,
  useDryRunExaminerGate,
} from '@/common/hooks/oralExam/useDryRunExaminerGate';

import type { LobbyStepProps } from './types';
import { WaitingHintCarousel } from './WaitingHintCarousel';

type ChecklistStatus = 'done' | 'pending' | 'warning';

type ChecklistItem = {
  key: string;
  label: string;
  status: ChecklistStatus;
};

const WAITING_PHASE_TICK_MS = 30_000;

function StatusIcon({ status }: { status: ChecklistStatus }) {
  switch (status) {
    case 'done':
      return <Icon as={CheckCircle} size="sm" color="$statusSuccess" testID="checklist-icon-done" />;
    case 'warning':
      return <Icon as={AlertCircle} size="sm" color="$statusWarning" testID="checklist-icon-warning" />;
    default:
      return <Icon as={Clock} size="md" color="$textSubtle" testID="checklist-icon-pending" />;
  }
}

function ChecklistRow({ item }: { item: ChecklistItem }) {
  return (
    <HStack
      alignItems="center"
      space="sm"
      testID={`oral-exam-waiting-checklist-${item.key}`}
    >
      <StatusIcon status={item.status} />
      <Text size="sm" color="$textBody">{item.label}</Text>
    </HStack>
  );
}

/**
 * Lobby waiting hold — recap checklist, advertised start, and one tip at a time.
 * Orientation film lives on {@link LobbyOrientationStep}, not here (A1).
 */
export function LobbyWaitingStep({
  runState,
  loungeHints = [],
  useProductDefaultHints = false,
  slotStartIso,
  onStartPractice,
  isStartingPractice = false,
  startPracticeError = null,
}: LobbyStepProps) {
  const { t } = useTranslation();
  const selfService = isSelfServiceRun(runState);
  const { isConnected, isReconnecting } = useMeetingConnectionState();
  const roomReady = isConnected && !isReconnecting;
  const { confirmBeforeStart } = useDryRunExaminerGate(selfService);
  const [confirmingCanary, setConfirmingCanary] = useState(false);
  const [unavailableAfterClick, setUnavailableAfterClick] = useState(false);
  const [phaseTick, setPhaseTick] = useState(0);

  useEffect(() => {
    if (selfService || !slotStartIso) return undefined;
    const id = setInterval(() => setPhaseTick((n) => n + 1), WAITING_PHASE_TICK_MS);
    return () => clearInterval(id);
  }, [selfService, slotStartIso]);

  const handleStartPractice = useCallback(async () => {
    if (isStartingPractice || confirmingCanary || !roomReady) return;
    setUnavailableAfterClick(false);
    setConfirmingCanary(true);
    try {
      const canaryOk = await confirmBeforeStart();
      if (!canaryOk) {
        setUnavailableAfterClick(true);
        return;
      }
      await Promise.resolve(onStartPractice());
    } catch {
      // startPracticeError / click-time unavailable surface the failure.
    } finally {
      setConfirmingCanary(false);
    }
  }, [confirmBeforeStart, confirmingCanary, isStartingPractice, onStartPractice, roomReady]);

  const idStatus = runState.idVerification?.status;
  const productDefaults = useMemo(
    () => ([
      {
        title: t('oralExam.lobby.waiting.defaultHints.coverParts.title'),
        body: t('oralExam.lobby.waiting.defaultHints.coverParts.body'),
      },
      {
        title: t('oralExam.lobby.waiting.defaultHints.example.title'),
        body: t('oralExam.lobby.waiting.defaultHints.example.body'),
      },
      {
        title: t('oralExam.lobby.waiting.defaultHints.whenClear.title'),
        body: t('oralExam.lobby.waiting.defaultHints.whenClear.body'),
      },
      {
        title: t('oralExam.lobby.waiting.defaultHints.sayTheShape.title'),
        body: t('oralExam.lobby.waiting.defaultHints.sayTheShape.body'),
      },
      {
        title: t('oralExam.lobby.waiting.defaultHints.useTheirWords.title'),
        body: t('oralExam.lobby.waiting.defaultHints.useTheirWords.body'),
      },
      {
        title: t('oralExam.lobby.waiting.defaultHints.nameTheApproach.title'),
        body: t('oralExam.lobby.waiting.defaultHints.nameTheApproach.body'),
      },
    ]),
    [t],
  );
  const displayHints = resolveWaitingLoungeHints(
    loungeHints,
    productDefaults,
    useProductDefaultHints,
  );
  const hasHints = displayHints.length > 0;

  const items = useMemo((): ChecklistItem[] => {
    const consentDone = Boolean(
      (runState as { consentGiven?: boolean }).consentGiven,
    ) || Boolean(runState.consentRecord);
    const { mediaState } = runState as { mediaState?: { screenShare?: { active?: boolean } } };
    const screenShareStatus: ChecklistStatus = !mediaState
      ? 'done'
      : mediaState.screenShare?.active
        ? 'done'
        : 'pending';

    let identityStatus: ChecklistStatus = 'pending';
    let identityLabel = t('oralExam.lobby.waiting.checklist.identity');
    if (idStatus === 'approved') {
      identityStatus = 'done';
    } else if (idStatus === 'submitted') {
      identityStatus = 'pending';
      identityLabel = t('oralExam.lobby.waiting.checklist.identityPending');
    }

    return [
      {
        key: 'camera',
        label: t('oralExam.lobby.waiting.checklist.camera'),
        status: 'done',
      },
      {
        key: 'consent',
        label: t('oralExam.lobby.waiting.checklist.consent'),
        status: consentDone ? 'done' : 'warning',
      },
      {
        key: 'screenShare',
        label: t('oralExam.lobby.waiting.checklist.screenShare'),
        status: screenShareStatus,
      },
      {
        key: 'identity',
        label: identityLabel,
        status: identityStatus,
      },
    ];
  }, [idStatus, runState, t]);

  const waitingTimePhase = useMemo((): OralExamWaitingTimePhase | null => {
    if (selfService || !slotStartIso) return null;
    const now = dayjs().add(phaseTick, 'millisecond').subtract(phaseTick, 'millisecond');
    return resolveOralExamWaitingTimePhase(slotStartIso, now);
  }, [selfService, slotStartIso, phaseTick]);

  const duringGrace = waitingTimePhase === 'during_grace';
  const isGroupExam = (runState.capacity ?? 1) > 1;

  const scheduledStartLabel = useMemo(() => {
    if (!slotStartIso) return null;
    const formatted = formatEnglishWaitingSlotStart(slotStartIso);
    if (!formatted) return null;
    return t('oralExam.lobby.waiting.scheduledStart', { time: formatted });
  }, [slotStartIso, t]);

  const graceStayCopy = useMemo(() => {
    if (!duringGrace || selfService || !slotStartIso) return null;
    if (isGroupExam) {
      return t('oralExam.lobby.waiting.graceStayGroup', {
        graceEndTime: formatOralExamJoinWindowCloseTime(slotStartIso),
      });
    }
    return t('oralExam.lobby.waiting.graceStayOneToOne');
  }, [duringGrace, isGroupExam, selfService, slotStartIso, t]);

  const showLegacyBody = !hasHints && !duringGrace && !selfService;
  const showLegacyWaitingHint = !selfService && !duringGrace;

  return (
    <VStack
      space="2xl"
      px="$6"
      alignItems="center"
      justifyContent="center"
      flex={1}
      testID="oral-exam-lobby-waiting-step"
    >
      <VStack
        space="xl"
        alignItems="center"
        width="100%"
        maxWidth={480}
        testID="oral-exam-waiting-core"
      >
        <VStack space="sm" alignItems="center" width="100%">
          <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
            {selfService
              ? t('oralExam.practice.waiting.title', {
                defaultValue: "You're ready to start",
              })
              : t('oralExam.lobby.waiting.title')}
          </Text>
          {scheduledStartLabel ? (
            <HStack
              space="sm"
              alignItems="center"
              testID="oral-exam-waiting-scheduled-start"
            >
              <Icon as={Clock} size="sm" color="$textSubtle" />
              <Text size="sm" color="$textSubtle">{scheduledStartLabel}</Text>
            </HStack>
          ) : null}
          {graceStayCopy ? (
            <Text
              size="md"
              color="$textBody"
              textAlign="center"
              testID={isGroupExam
                ? 'oral-exam-waiting-grace-stay-group'
                : 'oral-exam-waiting-grace-stay-one-to-one'}
            >
              {graceStayCopy}
            </Text>
          ) : null}
          {idStatus === 'submitted' ? (
            <Text
              size="sm"
              color="$textSubtle"
              textAlign="center"
              testID="oral-exam-waiting-id-review-note"
            >
              {t('oralExam.lobby.orientation.reviewNote')}
            </Text>
          ) : null}
          {showLegacyBody ? (
            <Text size="md" color="$textBody" textAlign="center">
              {t('oralExam.lobby.waiting.body')}
            </Text>
          ) : null}
          {selfService && !hasHints ? (
            <Text size="md" color="$textBody" textAlign="center">
              {t('oralExam.practice.waiting.body', {
                defaultValue:
                  "You're all set. Start when you are — the AI examiner joins with you.",
              })}
            </Text>
          ) : null}
        </VStack>

        <VStack space="sm" width="100%" maxWidth={280} testID="oral-exam-waiting-checklist">
          {items.map((item) => (
            <ChecklistRow key={item.key} item={item} />
          ))}
        </VStack>
      </VStack>

      {hasHints ? (
        <Box pt="$12" width="100%" alignItems="center">
          <WaitingHintCarousel hints={displayHints} />
        </Box>
      ) : null}
      {selfService && onStartPractice ? (
        <VStack space="sm" width="100%" maxWidth={320} alignItems="center">
          {confirmingCanary ? (
            <Text size="sm" color="$textSubtle" textAlign="center" testID="oral-exam-start-practice-checking">
              {t('oralExam.practice.examinerChecking', {
                defaultValue: 'Checking that the AI examiner is available…',
              })}
            </Text>
          ) : null}
          {selfService && !roomReady && !confirmingCanary ? (
            <Text size="sm" color="$textSubtle" textAlign="center" testID="oral-exam-start-practice-joining">
              {t('oralExam.practice.joiningRoom', {
                defaultValue: 'Joining the exam room…',
              })}
            </Text>
          ) : null}
          <Button
            action="primary"
            size="lg"
            width="$full"
            onPress={() => {
              handleStartPractice().catch(() => undefined);
            }}
            isDisabled={isStartingPractice || confirmingCanary || !roomReady}
            testID="oral-exam-start-practice"
          >
            <ButtonText>
              {isStartingPractice || confirmingCanary
                ? t('oralExam.practice.starting', { defaultValue: 'Starting…' })
                : t('oralExam.practice.startCta', { defaultValue: 'Start dry run' })}
            </ButtonText>
          </Button>
          {unavailableAfterClick ? (
            <Text size="sm" color="$statusError" textAlign="center" testID="oral-exam-start-practice-unavailable">
              {t('oralExam.practice.unavailable', {
                defaultValue: DRY_RUN_UNAVAILABLE_MESSAGE,
              })}
            </Text>
          ) : null}
          {startPracticeError ? (
            <Text size="sm" color="$statusError" textAlign="center" testID="oral-exam-start-practice-error">
              {startPracticeError}
            </Text>
          ) : null}
        </VStack>
      ) : null}
      {showLegacyWaitingHint ? (
        <Text size="sm" color="$textSubtle" textAlign="center" testID="oral-exam-waiting-hint">
          {t('oralExam.lobby.waiting.waitingForExaminer')}
        </Text>
      ) : null}
    </VStack>
  );
}
